import type { SxProps, Theme } from '@mui/material';

import { styles, theme } from '../theme/theme';

/**
 * Listings requested per homepage section. Seven is the most any breakpoint
 * shows; narrower ones hide the surplus in CSS rather than refetching.
 *
 * The generated API types `limit` as a string, and RTK Query keys its cache on
 * the serialised arg - `'7'` and `7` are different cache entries, so a mismatch
 * between here and the page's SSR prefetch silently wastes the prefetch.
 */
export const HOME_LISTING_LIMIT = '7';

/**
 * ListingCard reserves its image with padding-bottom against the card's own
 * width, so the box is held before the image resolves. Shared so the loading
 * placeholder reserves the same slot.
 */
export const LISTING_IMAGE_ASPECT_PADDING = '75%';

/**
 * Listing cards are sized xs={12} sm={4} lg={3}, so a row holds this many.
 */
const COLUMNS_PER_ROW = { xs: 1, sm: 3, lg: 4 } as const;

/**
 * Phones: below this viewport width a full-width card is at most
 * PHONE_MAX_VIEWPORT less the section's xs margin and padding (20px each
 * side, see HomePageCard), which keeps 2x phones on an 800px image rather
 * than the 1600px the whole xs range (up to 800px wide) would need.
 */
const PHONE_MAX_VIEWPORT = 480;
const XS_SECTION_INSET = 80;

const { sm, lg, xl } = theme.breakpoints.values;

/**
 * The widest a listing card's image renders (CSS px) from each viewport width
 * up: the next step's width - or the page's max width, past the last one -
 * split across that breakpoint's columns. ListingCard hands these to
 * AspectImageLoader, which turns them into 1x/2x image-set() candidates so the
 * browser picks one size before downloading anything.
 */
export const LISTING_CARD_IMAGE_WIDTHS = [
  { minWidth: 0, width: PHONE_MAX_VIEWPORT - XS_SECTION_INSET },
  { minWidth: PHONE_MAX_VIEWPORT, width: sm / COLUMNS_PER_ROW.xs },
  { minWidth: sm, width: lg / COLUMNS_PER_ROW.sm },
  { minWidth: lg, width: xl / COLUMNS_PER_ROW.lg },
  { minWidth: xl, width: styles.maxWidth / COLUMNS_PER_ROW.lg },
];

/**
 * Listings shown per section at each breakpoint, picked so that the listings
 * plus the trailing "view all" card always come to a whole number of rows:
 * 8 slots divides by 4 at lg, 6 divides by 3 at sm, and every count fills a
 * row at xs because cards are full width there.
 */
export const HOME_VISIBLE_LISTINGS = { xs: 7, sm: 5, lg: 7 } as const;

type Breakpoint = keyof typeof HOME_VISIBLE_LISTINGS;
const BREAKPOINTS = Object.keys(HOME_VISIBLE_LISTINGS) as Breakpoint[];

/**
 * Grid slots the widest breakpoint reserves for one section: its listings plus
 * the "view all" card. Used to size the loading placeholder.
 */
export const HOME_SECTION_SLOTS =
  Math.max(...Object.values(HOME_VISIBLE_LISTINGS)) + 1;

/**
 * How many of `total` listings a section shows at one breakpoint.
 *
 * The cap keeps rows whole for a full-length section. On top of that, a
 * section whose listings plus the "view all" card leave exactly one item on
 * the final row would strand that card there alone, so one listing is dropped
 * to close the row: four boats at lg show three, filling a single row of four.
 */
const visibleListingCount = (total: number, bp: Breakpoint) => {
  const capped = Math.min(total, HOME_VISIBLE_LISTINGS[bp]);
  const strandsTheCard = (capped + 1) % COLUMNS_PER_ROW[bp] === 1;
  return strandsTheCard ? Math.max(capped - 1, 0) : capped;
};

/**
 * Hides a listing at the breakpoints that would leave it in a ragged row.
 * Returned as responsive CSS rather than a JS media query so it applies on the
 * first paint - trimming the count in JS would re-flow after mount.
 *
 * Returns undefined for cards visible everywhere, to keep the sx prop off the
 * majority of items.
 */
export const listingVisibilitySx = (
  index: number,
  total: number
): SxProps<Theme> | undefined => {
  const display = Object.fromEntries(
    BREAKPOINTS.map((bp) => [
      bp,
      index < visibleListingCount(total, bp) ? 'block' : 'none',
    ])
  );
  const alwaysVisible = Object.values(display).every((v) => v === 'block');
  return alwaysVisible ? undefined : { display };
};

/**
 * The number of columns a row holds at the given breakpoint. Exported for
 * tests and for reasoning about the counts above.
 */
export const homeColumnsPerRow = (bp: Breakpoint) => COLUMNS_PER_ROW[bp];
